import { User } from "firebase/auth";
import {
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentReference,
  getDocs,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { useEffect } from "react";
import { useDashboard } from "@/lib/store";
import {
  IDashboardStore,
  ILayout,
} from "@/domain/interfaces/dashboard.interface";
import { db } from "@/lib/firebase";

export const getDashboardDocRef = (user?: User) => {
  if (user?.uid) {
    return doc(
      db,
      "dashboards2",
      user.uid,
    ) as DocumentReference<IDashboardStore>;
  } else {
    return null;
  }
};

export const getLayoutDocRef = (
  dashboardRef: DocumentReference<IDashboardStore>,
  id: string,
) => {
  if (dashboardRef) {
    return doc(dashboardRef, "layouts", id) as DocumentReference<ILayout>;
  } else {
    return null;
  }
};

export const getLayoutsCollectionRef = (
  dashboardRef: DocumentReference<IDashboardStore>,
) => {
  if (dashboardRef) {
    return collection(dashboardRef, "layouts") as CollectionReference<ILayout>;
  } else {
    return null;
  }
};

export const batchRemoveFromFirestore = async (
  dashboardRef: DocumentReference<IDashboardStore>,
  layoutIds: string[],
) => {
  if (!dashboardRef || layoutIds.length === 0) {
    return;
  }

  const batch = writeBatch(db);

  layoutIds.forEach((id) => {
    const ref = getLayoutDocRef(dashboardRef, id);
    if (ref) {
      batch.delete(ref);
    }
  });

  await batch.commit();
};

export const saveLayoutToFirestore = async (
  dashboardRef: DocumentReference<IDashboardStore>,
  layout: ILayout,
) => {
  const ref = getLayoutDocRef(dashboardRef, layout.id);
  if (ref) {
    await setDoc(ref, layout, { merge: true });
  }
};

export const useFirebaseLayouts = (user: User | undefined) => {
  const updateDashboardState = useDashboard(
    (state) => state.updateDashboardState,
  );
  const layouts = useDashboard((state) => state.layouts);
  const layoutsOrder = useDashboard((state) => state.layoutsOrder);
  const activeLayoutId = useDashboard((state) => state.activeLayoutId);

  // Load layouts from Firebase
  useEffect(() => {
    const loadLayouts = async () => {
      if (!user) {
        return;
      }

      const dashboardRef = getDashboardDocRef(user);
      if (!dashboardRef) {
        return;
      }

      try {
        const layoutsCollectionRef = collection(dashboardRef, "layouts");
        const layoutsSnapshot = await getDocs(layoutsCollectionRef);

        if (!layoutsSnapshot.empty) {
          updateDashboardState((draft) => {
            // Create a map of existing layouts by ID for quick lookup
            const existingLayoutsMap = new Map(
              draft.layouts.map((layout) => [layout.id, layout]),
            );

            layoutsSnapshot.forEach((doc) => {
              const firebaseLayout = doc.data() as ILayout;
              const existingLayout = existingLayoutsMap.get(firebaseLayout.id);

              if (existingLayout) {
                // Update existing layout with Firebase data
                Object.assign(existingLayout, firebaseLayout);
              } else {
                // Add new layout from Firebase
                draft.layouts.push(firebaseLayout);
                draft.layoutsOrder.push(firebaseLayout.id);
              }
            });

            // Set active layout if none is set
            if (!draft.activeLayoutId && draft.layouts.length > 0) {
              draft.activeLayoutId = draft.layouts[0].id;
            }
          });
        }
      } catch (error) {
        console.error("Error loading layouts from Firebase:", error);
      }
    };

    loadLayouts();
  }, [user, updateDashboardState]);

  // Save layouts to Firebase when they change
  useEffect(() => {
    const saveLayouts = async () => {
      if (!user) {
        return;
      }

      const dashboardRef = getDashboardDocRef(user);
      if (!dashboardRef) {
        return;
      }

      try {
        // Save each layout to Firebase
        await Promise.all(
          layouts.map((layout) => saveLayoutToFirestore(dashboardRef, layout)),
        );
      } catch (error) {
        console.error("Error saving layouts to Firebase:", error);
      }
    };

    saveLayouts();
  }, [layouts, user]);

  // Handle layout removal
  useEffect(() => {
    const removeDeletedLayouts = async () => {
      if (!user) {
        return;
      }

      const dashboardRef = getDashboardDocRef(user);
      if (!dashboardRef) {
        return;
      }

      try {
        const layoutsCollectionRef = getLayoutsCollectionRef(dashboardRef);
        if (!layoutsCollectionRef) {
          return;
        }

        const snapshot = await getDocs(layoutsCollectionRef);
        const firestoreLayoutIds = snapshot.docs.map((doc) => doc.id);
        const currentLayoutIds = new Set(layouts.map((layout) => layout.id));

        // Find layouts that exist in Firestore but not in current state
        const layoutsToRemove = firestoreLayoutIds.filter(
          (id) => !currentLayoutIds.has(id),
        );

        if (layoutsToRemove.length > 0) {
          await batchRemoveFromFirestore(dashboardRef, layoutsToRemove);
        }
      } catch (error) {
        console.error("Error syncing layout removals with Firestore:", error);
      }
    };

    removeDeletedLayouts();
  }, [user, layouts]);

  return {};
};
